import { useCallback, useEffect, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import React from "react";

/**
 * Hook + button used to toggle any map container into fullscreen mode.
 *
 * We deliberately avoid the browser's native `Element.requestFullscreen()`
 * API because it triggers a heavyweight OS-level fullscreen (hides the
 * tab strip, browser chrome, address bar) that EOC operators don't want.
 * They want a maximised IN-PAGE experience that still keeps their
 * other browser windows / WebSocket alert tabs visible.
 *
 * Implementation: a boolean state flips the wrapping `<div>` between
 * its in-flow layout and a `fixed inset-0 z-[9999]` overlay. MapLibre's
 * internal ResizeObserver picks up the size change; we also call
 * `map.resize()` explicitly on the next animation frame as a belt +
 * braces guarantee.
 *
 * Usage:
 *   const { isFs, toggle, fsWrapperClass } = useMapFullscreen(mapRef);
 *   return (
 *     <div className={fsWrapperClass("relative w-full h-[55vh]")}>
 *       <div ref={containerRef} ... />
 *       <MapFullscreenButton isFs={isFs} onToggle={toggle} className="absolute top-3 right-3 z-[450]" />
 *     </div>
 *   );
 */
export function useMapFullscreen(mapRef) {
  const [isFs, setIsFs] = useState(false);

  const toggle = useCallback(() => setIsFs((v) => !v), []);
  const exit = useCallback(() => setIsFs(false), []);

  // Resize the underlying MapLibre canvas on the next frame so it
  // tracks the new container size. Done unconditionally on toggle
  // because we want both enter AND exit to trigger a resize.
  useEffect(() => {
    const m = mapRef?.current;
    if (!m || typeof m.resize !== "function") return undefined;
    const id = requestAnimationFrame(() => {
      try { m.resize(); } catch (_e) { /* noop */ }
    });
    // One more resize a beat later in case CSS transitions are in flight.
    const t = setTimeout(() => {
      try { m.resize(); } catch (_e) { /* noop */ }
    }, 220);
    return () => { cancelAnimationFrame(id); clearTimeout(t); };
  }, [isFs, mapRef]);

  // Esc exits fullscreen — match the native UX expectation.
  useEffect(() => {
    if (!isFs) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setIsFs(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFs]);

  // Helper that returns the right class list for the wrapper given
  // its normal (in-flow) classes. Falls back to the in-flow classes
  // when not fullscreen so callers can compose with their existing
  // sizing (e.g. `h-[55vh] lg:flex-1`).
  const fsWrapperClass = (inflowClasses) =>
    isFs
      ? "fixed inset-0 z-[9999] bg-[#0b0f14]"
      : inflowClasses;

  return { isFs, toggle, exit, fsWrapperClass };
}

export const MapFullscreenButton = ({ isFs, onToggle, className = "" }) => (
  <button
    type="button"
    data-testid="map-fullscreen-toggle"
    onClick={onToggle}
    title={isFs ? "Quitter le plein écran (Échap)" : "Plein écran"}
    aria-label={isFs ? "Quitter le plein écran" : "Plein écran"}
    className={`bg-[#111418]/90 backdrop-blur border border-zinc-800 hover:border-emerald-400/60 text-zinc-300 hover:text-emerald-300 p-1.5 transition-colors ${className}`}
  >
    {isFs ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
  </button>
);
